interface RateLimitAttempt {
  count: number;
  lastAttempt: number;
  isBlocked: boolean;
  blockUntil?: number;
}

const ATTEMPT_STORAGE_KEY = 'auth_attempts';
const MAX_ATTEMPTS = 5;
const BLOCK_DURATION_MS = 15 * 60 * 1000; // 15 minutes
const RESET_WINDOW_MS = 60 * 60 * 1000; // 1 hour

export class RateLimiter {
  private getAttempts(): RateLimitAttempt {
    try {
      const stored = localStorage.getItem(ATTEMPT_STORAGE_KEY);
      if (!stored) {
        return { count: 0, lastAttempt: 0, isBlocked: false };
      }
      return JSON.parse(stored);
    } catch {
      return { count: 0, lastAttempt: 0, isBlocked: false };
    }
  }

  private saveAttempts(attempts: RateLimitAttempt): void {
    try {
      localStorage.setItem(ATTEMPT_STORAGE_KEY, JSON.stringify(attempts));
    } catch {
      // Silently handle localStorage errors
    }
  }

  canAttempt(): { allowed: boolean; remainingTime?: number; attemptsRemaining?: number } {
    const attempts = this.getAttempts();
    const now = Date.now();

    // Check if block period has expired
    if (attempts.isBlocked && attempts.blockUntil && now >= attempts.blockUntil) {
      this.reset();
      return { allowed: true, attemptsRemaining: MAX_ATTEMPTS };
    }

    // If currently blocked
    if (attempts.isBlocked && attempts.blockUntil) {
      const remainingTime = Math.ceil((attempts.blockUntil - now) / 1000);
      return { allowed: false, remainingTime };
    }

    // Check if we should reset the counter (after 1 hour)
    if (now - attempts.lastAttempt > RESET_WINDOW_MS) {
      this.reset();
      return { allowed: true, attemptsRemaining: MAX_ATTEMPTS };
    }

    // Check if we've exceeded the limit
    if (attempts.count >= MAX_ATTEMPTS) {
      const blockUntil = now + BLOCK_DURATION_MS;
      this.saveAttempts({
        ...attempts,
        isBlocked: true,
        blockUntil
      });
      return { allowed: false, remainingTime: Math.ceil(BLOCK_DURATION_MS / 1000) };
    }

    return { 
      allowed: true, 
      attemptsRemaining: MAX_ATTEMPTS - attempts.count 
    };
  }

  recordFailedAttempt(): void {
    const attempts = this.getAttempts();
    const now = Date.now();

    const newAttempts: RateLimitAttempt = {
      count: attempts.count + 1,
      lastAttempt: now,
      isBlocked: false
    };

    // If this puts us over the limit, block immediately
    if (newAttempts.count >= MAX_ATTEMPTS) {
      newAttempts.isBlocked = true;
      newAttempts.blockUntil = now + BLOCK_DURATION_MS;
    }

    this.saveAttempts(newAttempts);
  }

  recordSuccessfulAttempt(): void {
    this.reset();
  }

  reset(): void {
    this.saveAttempts({ count: 0, lastAttempt: 0, isBlocked: false });
  }

  getRemainingAttempts(): number {
    const attempts = this.getAttempts();
    return Math.max(0, MAX_ATTEMPTS - attempts.count);
  }
}

export const authRateLimiter = new RateLimiter();